import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SiteSurvey,
  SiteSurveyWithSurveyor,
  SiteSurveysListResponse,
  SiteSurveyResponse,
  SurveyStatus,
  CreateSiteSurveyInput,
  UpdateSiteSurveyInput,
  VulnerabilityFinding,
  VulnerabilityFindingsListResponse,
  VulnerabilityFindingResponse,
  FindingStatus,
  FindingSeverity,
  FindingCategory,
  CreateVulnerabilityFindingInput,
  UpdateVulnerabilityFindingInput,
  SitesListResponse,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------------------------------------------------------------------------
// Site surveys
// ---------------------------------------------------------------------------

export interface SurveyFilters {
  site_id?: number;
  status?: SurveyStatus;
}

export function useSurveys(filters: SurveyFilters = {}) {
  const params = new URLSearchParams();
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/surveys?${qs}` : '/api/surveys';
  return useQuery({
    queryKey: ['surveys', { ...filters }],
    queryFn: ({ signal }) => api.get<SiteSurveysListResponse>(url, signal),
  });
}

export function useSurvey(id: number | null) {
  return useQuery({
    queryKey: ['survey', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<SiteSurveyResponse>(`/api/surveys/${id}`, signal),
  });
}

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiteSurveyInput) =>
      api.post<{ survey: SiteSurveyWithSurveyor }>('/api/surveys', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export interface UpdateSurveyVars extends UpdateSiteSurveyInput {
  id: number;
}

export function useUpdateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateSurveyVars) =>
      api.patch<{ survey: SiteSurveyWithSurveyor }>(`/api/surveys/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void qc.invalidateQueries({ queryKey: ['survey', vars.id] });
    },
  });
}

// Drives the draft -> submitted -> approved workflow. Gated server-side on
// survey.approve (separate from survey.write), so the page only surfaces this
// control when the user holds that permission.
export function useTransitionSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: SurveyStatus }) =>
      api.post<{ survey: SiteSurveyWithSurveyor }>(
        `/api/surveys/${id}/transition`,
        { status },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void qc.invalidateQueries({ queryKey: ['survey', vars.id] });
    },
  });
}

export function useDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/surveys/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Vulnerability findings (nested under a survey)
// ---------------------------------------------------------------------------

export interface FindingFilters {
  status?: FindingStatus;
  severity?: FindingSeverity;
  category?: FindingCategory;
}

export function useFindings(surveyId: number | null, filters: FindingFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.category) params.set('category', filters.category);
  const qs = params.toString();
  const base = `/api/surveys/${surveyId}/findings`;
  const url = qs.length > 0 ? `${base}?${qs}` : base;
  return useQuery({
    queryKey: ['findings', surveyId, { ...filters }],
    enabled: surveyId !== null,
    queryFn: ({ signal }) =>
      api.get<VulnerabilityFindingsListResponse>(url, signal),
  });
}

export function useCreateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      surveyId,
      ...input
    }: { surveyId: number } & CreateVulnerabilityFindingInput) =>
      api.post<{ finding: VulnerabilityFinding }>(
        `/api/surveys/${surveyId}/findings`,
        input,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['findings', vars.surveyId] });
    },
  });
}

export function useUpdateFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      surveyId,
      findingId,
      ...patch
    }: { surveyId: number; findingId: number } & UpdateVulnerabilityFindingInput) =>
      api.patch<{ finding: VulnerabilityFinding }>(
        `/api/surveys/${surveyId}/findings/${findingId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['findings', vars.surveyId] });
    },
  });
}

export function useDeleteFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, findingId }: { surveyId: number; findingId: number }) =>
      api.delete<void>(`/api/surveys/${surveyId}/findings/${findingId}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['findings', vars.surveyId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sites lookup — used to populate the site picker on the create-survey form
// and to resolve a survey's site name for display. Kept local to this module
// (a dedicated sites query module is wired during Integration).
// ---------------------------------------------------------------------------

export function useSitesLookup() {
  return useQuery({
    queryKey: ['surveys', 'sites-lookup'],
    queryFn: ({ signal }) => api.get<SitesListResponse>('/api/sites', signal),
    staleTime: 60_000,
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type { SiteSurvey, SiteSurveyWithSurveyor, VulnerabilityFinding };
